import axios from 'axios';
import { auth } from '../firebase/firebase';
import config from '../config';
import { isBillingBlockedError, emitBillingBlocked, billingBlockedFromError } from '../utils/billingEvents';
import { redirectToMfaSetup } from '../utils/mfaRedirect';

// Retry configuration
const RETRY_CONFIG = {
    maxRetries: 3,
    baseDelay: 1000,  // Base delay in ms
    // Status codes that should trigger a retry
    retryableStatuses: [408, 429, 500, 502, 503, 504],
    // HTTP methods that are safe to retry
    retryableMethods: ['get', 'put', 'post', 'delete'],
};

// Calculate delay with exponential backoff and jitter
const getRetryDelay = (attempt) => {
    const exponentialDelay = RETRY_CONFIG.baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 1000;  // Add up to 1s of jitter
    return exponentialDelay + jitter;
};

// Check if a request should be retried
const shouldRetry = (error, retryCount) => {
    if (retryCount >= RETRY_CONFIG.maxRetries) {
        return false;
    }

    const method = error.config?.method?.toLowerCase();
    if (!RETRY_CONFIG.retryableMethods.includes(method)) {
        return false;
    }

    // Retry on network errors
    if (!error.response) {
        return true;
    }

    // Retry on specific status codes
    return RETRY_CONFIG.retryableStatuses.includes(error.response?.status);
};

// Create custom axios instance
const api = axios.create({
    baseURL: config.baseURL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
    // Increase timeout to 2 minutes for file uploads
    timeout: 120000,  // 120 seconds
});

// Add auth token to requests and initialize retry tracking
api.interceptors.request.use(async (config) => {
    // Initialize retry count if not set
    if (config._retryCount === undefined) {
        config._retryCount = 0;
    }

    // Set longer timeout for upload endpoints
    if (config.url === '/upload' || config.url?.includes('/upload-url') || config.url?.includes('/finalize-upload')) {
        config.timeout = 300000; // 5 minutes for uploads
    }

    // Set longer timeout for process-case endpoint
    if (config.url === '/case/process-case') {
        config.timeout = 600000; // 10 minutes to match workflow timeout
    }

    if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
    }

    // For FormData, remove Content-Type to let browser set it
    if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
    }
    return config;
});

// Add response interceptor to handle errors and retry logic
api.interceptors.response.use(
    response => response,
    async (error) => {
        const config = error.config;

        // Log error details
        console.error('API Error:', error);
        if (error.code === 'ERR_NETWORK') {
            console.error('Network error details:', {
                url: config?.url,
                method: config?.method,
                baseURL: config?.baseURL,
                message: error.message
            });
        }

        // A3 billing gate: never retried (402 is not in retryableStatuses);
        // surface it app-wide through BillingBlockedModal.
        if (isBillingBlockedError(error)) {
            emitBillingBlocked(billingBlockedFromError(error));
            return Promise.reject(error);
        }

        // C2 MFA gate: the admin routes answer 403 mfa_required when the ID
        // token carries no second factor. Never retried; the enrollment page
        // is the only fix, so send the browser there.
        if (error.response?.status === 403 && error.response?.data?.code === 'mfa_required') {
            redirectToMfaSetup();
            return Promise.reject(error);
        }

        // Check if we should retry
        if (config && shouldRetry(error, config._retryCount)) {
            config._retryCount += 1;
            const delay = getRetryDelay(config._retryCount);

            console.warn(
                `Retrying request (${config._retryCount}/${RETRY_CONFIG.maxRetries}) after ${Math.round(delay)}ms:`,
                config.url
            );

            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, delay));

            // Retry the request
            return api(config);
        }

        return Promise.reject(error);
    }
);

export const facilityApi = {
    list: () => api.get('/admin/facility').then((r) => r.data),
    get: (id) => api.get(`/admin/facility/${id}`).then((r) => r.data),
    create: (payload) => api.post('/admin/facility', payload).then((r) => r.data),
    update: (id, payload) => api.put(`/admin/facility/${id}`, payload).then((r) => r.data),
    listMembers: (id) => api.get(`/admin/facility/${id}/members`).then((r) => r.data),
    assignMember: (id, payload) => api.post(`/admin/facility/${id}/members`, payload).then((r) => r.data),
    changeRole: (id, uid, payload) => api.put(`/admin/facility/${id}/members/${uid}`, payload).then((r) => r.data),
    removeMember: (id, uid) => api.delete(`/admin/facility/${id}/members/${uid}`).then((r) => r.data),
    createInvite: (id, payload) => api.post(`/admin/facility/${id}/invites`, payload).then((r) => r.data),
    listInvites: (id) => api.get(`/admin/facility/${id}/invites`).then((r) => r.data),
    revokeInvite: (id, code) => api.delete(`/admin/facility/${id}/invites/${code}`).then((r) => r.data),
    reassignCase: (id, caseId, payload) => api.post(`/admin/facility/${id}/cases/${caseId}/reassign`, payload).then((r) => r.data),
    // Reassigns every case the member holds; large caseloads can exceed the default 120 s. Idempotent, so a retry is safe.
    offboardMember: (id, uid, payload) => api.post(`/admin/facility/${id}/members/${uid}/offboard`, payload, { timeout: 300_000 }).then((r) => r.data),
};

export const auditApi = {
    listForFacility: (id, params) => api.get(`/admin/facility/${id}/audit`, { params }).then((r) => r.data),
    listAll: (params) => api.get('/admin/audit', { params }).then((r) => r.data),
};

export default api;
